import React, { useState } from "react";
import PropTypes from "prop-types";
import { Tabs, Tab, Stack, Chip } from "@mui/material";
import { Icon } from "@iconify/react";
import CollapsibleCard from "./CollapsibleCard";
import ChargerStatusPanel, { needsAttention } from "./ChargerStatusPanel";
import { ChargerUptimeBody, UPTIME_PERIOD_OPTIONS } from "./ChargerUptimeV2";
import TimePeriodSelector from "../../common/TimePeriodSelector";

// Chargers: live status and uptime in one section, as two tabs. The header stays
// fixed; the uptime period selector sits at the right of the tab row.

const TAB_KEY = "dashboardV2.chargersTab";
// Outline inside the tab: the tab strip clips anything drawn outside it.
const TAB_SX = { minHeight: 36, textTransform: "none", fontWeight: 600, "&:focus-visible": { outlineOffset: -2 } };

const loadTab = () => {
  try {
    return window.localStorage.getItem(TAB_KEY) === "uptime" ? "uptime" : "status";
  } catch (_) {
    return "status";
  }
};

// `chargepointIds`: set when a site is selected — the Uptime tab shows only those.
const ChargersV2 = ({ chargers, chargepointIds = null, isFetching = false, teamId }) => {
  const [tab, setTab] = useState(loadTab);
  const [period, setPeriod] = useState("Last7Days");
  const list = Array.isArray(chargers) ? chargers : [];
  // Same rule as the Status tab's Offline tile, so the two tabs agree.
  const offlineNow = list.filter((c) => c?.status !== "Active").length;
  const attention = list.filter(needsAttention).length;

  const changeTab = (_, next) => {
    setTab(next);
    try {
      window.localStorage.setItem(TAB_KEY, next);
    } catch (_) {
      /* storage unavailable - ignore */
    }
  };

  return (
    <CollapsibleCard
      title="Chargers"
      subtitle="Live status of every charger and how long each stayed connected"
      icon={<Icon icon="eva:flash-outline" width={22} height={22} />}
    >
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        spacing={1}
        sx={{ mb: 2, flexWrap: "wrap", rowGap: 1 }}
      >
        <Tabs value={tab} onChange={changeTab} sx={{ minHeight: 36 }}>
          <Tab
            value="status"
            sx={TAB_SX}
            label={
              <Stack direction="row" alignItems="center" spacing={0.75}>
                <span>Status</span>
                {!isFetching && attention > 0 && (
                  <Chip
                    size="small"
                    color="warning"
                    label={`${attention} need${attention === 1 ? "s" : ""} attention`}
                    sx={{ height: 20, fontSize: "0.7rem", fontWeight: 600 }}
                  />
                )}
              </Stack>
            }
          />
          <Tab value="uptime" sx={TAB_SX} label="Uptime" />
        </Tabs>
        {tab === "uptime" && (
          <TimePeriodSelector value={period} onChange={setPeriod} options={UPTIME_PERIOD_OPTIONS} />
        )}
      </Stack>
      {tab === "status" ? (
        <ChargerStatusPanel chargers={chargers} isFetching={isFetching} teamId={teamId} embedded />
      ) : (
        <ChargerUptimeBody
          key={`uptime-${teamId}`}
          teamId={teamId}
          period={period}
          offlineNow={isFetching ? undefined : offlineNow}
          chargepointIds={chargepointIds}
        />
      )}
    </CollapsibleCard>
  );
};

ChargersV2.propTypes = {
  chargers: PropTypes.array,
  chargepointIds: PropTypes.instanceOf(Set),
  isFetching: PropTypes.bool,
  teamId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

export default ChargersV2;
